import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/Dialog';
import type { DocumentTemplateType, DocumentTemplateField, DocumentLogoSource } from '../../../core/models/Document';
import { useClients } from '../../../core/hooks/useClients';
import { useOrganizations } from '../../../core/hooks/useOrganizations';
import { useAuth } from '../../../core/contexts/AuthContext';
import { loadTemplateContent, getTemplateContent, processTemplate } from '../../../core/utils/templateLoader';
import { findClientAccountForProject, resolveProjectContactEmail } from '../../../core/utils/projectContact';
import { Eye } from 'lucide-react';
import { useCurrentOrganization } from '../../../core/hooks/useCurrentOrganization';
import { logError } from '../../../lib/utils';

interface DocumentGeneratorProps {
  template: DocumentTemplateType;
  onGenerate: (data: {
    title: string;
    clientId: string;
    dynamicFields: Record<string, any>;
    logoSource: DocumentLogoSource;
  }) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const DocumentGenerator: React.FC<DocumentGeneratorProps> = ({
  template,
  onGenerate,
  onCancel,
  isSubmitting: externalIsSubmitting,
}) => {
  const { organizationId } = useAuth();
  const { clients, loading: clientsLoading } = useClients(organizationId);
  const { organizations } = useOrganizations();
  const { organization } = useCurrentOrganization(organizationId);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<Record<string, any>>({
    defaultValues: {
      title: '',
      clientId: '',
      useOrgLogo: true,
      useClientLogo: false,
      ...template.dynamicFields.reduce((acc, field) => {
        acc[field.key] = field.defaultValue || '';
        return acc;
      }, {} as Record<string, any>),
    },
    mode: 'onChange',
  });

  // Register clientId and select fields with validation
  React.useEffect(() => {
    register('clientId', { required: 'Please select a project' });
    // Register all select fields
    template.dynamicFields.forEach((field) => {
      if (field.type === 'select' && field.required) {
        register(field.key, { required: `${field.label} is required` });
      }
    });
  }, [register, template.dynamicFields]);

  const selectedClientId = watch('clientId');
  const useOrgLogo = watch('useOrgLogo');
  const useClientLogo = watch('useClientLogo');

  // Auto-populate client-related fields when client is selected
  React.useEffect(() => {
    if (!selectedClientId) return;

    const selectedClient = clients.find((c) => c.id === selectedClientId);
    if (!selectedClient) return;
    const clientAccount = findClientAccountForProject(selectedClient, organizations);
    const contactEmail = resolveProjectContactEmail(selectedClient, clientAccount);

    template.dynamicFields.forEach((field) => {
      if (field.type === 'client') {
        if (field.key === 'client_name') {
          setValue(field.key, selectedClient.name, { shouldValidate: false });
        }
        if (field.key === 'client_email') {
          setValue(field.key, contactEmail, { shouldValidate: false });
        }
      }
    });
  }, [selectedClientId, clients, organizations, template.dynamicFields, setValue]);

  const renderField = (field: DocumentTemplateField) => {
    const fieldValue = watch(field.key);

    switch (field.type) {
      case 'text':
        return (
          <Input
            key={field.key}
            label={field.label}
            {...register(field.key, {
              required: field.required ? `${field.label} is required` : false,
            })}
            error={errors[field.key]?.message as string | undefined}
            placeholder={field.placeholder}
          />
        );

      case 'number':
        return (
          <Input
            key={field.key}
            label={field.label}
            type="number"
            {...register(field.key, {
              required: field.required ? `${field.label} is required` : false,
              valueAsNumber: true,
            })}
            error={errors[field.key]?.message as string | undefined}
            placeholder={field.placeholder}
          />
        );

      case 'date':
        return (
          <Input
            key={field.key}
            label={field.label}
            type="date"
            {...register(field.key, {
              required: field.required ? `${field.label} is required` : false,
            })}
            error={errors[field.key]?.message as string | undefined}
          />
        );

      case 'boolean':
        return (
          <div key={field.key} className="flex items-center space-x-2">
            <Checkbox
              {...register(field.key)}
              onCheckedChange={(checked) => setValue(field.key, checked === true)}
            />
            <label className="text-sm font-medium">{field.label}</label>
          </div>
        );

      case 'select':
        return (
          <div key={field.key} className="space-y-2">
            <label className="text-sm font-medium">
              {field.label}
              {field.required && <span className="text-error ml-1">*</span>}
            </label>
            <Select
              value={watch(field.key) || ''}
              onValueChange={(value) => {
                setValue(field.key, value, { shouldValidate: true });
              }}
              required={field.required}
            >
              <SelectTrigger className={errors[field.key] ? 'border-error' : ''}>
                <SelectValue placeholder={field.placeholder || 'Select an option'} />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors[field.key] && (
              <p className="text-sm text-error">{String(errors[field.key]?.message || '')}</p>
            )}
          </div>
        );

      case 'multiline':
        return (
          <Textarea
            key={field.key}
            label={field.label}
            {...register(field.key, {
              required: field.required ? `${field.label} is required` : false,
            })}
            error={errors[field.key]?.message as string | undefined}
            placeholder={field.placeholder}
            rows={4}
          />
        );

      case 'client': {
        // Auto-populate from selected project / linked client account
        const selectedClient = clients.find((c) => c.id === selectedClientId);
        const clientAccount = findClientAccountForProject(selectedClient, organizations);
        const clientFieldValue = selectedClient
          ? field.key === 'client_name'
            ? selectedClient.name
            : field.key === 'client_email'
            ? resolveProjectContactEmail(selectedClient, clientAccount)
            : (fieldValue as string)
          : (fieldValue as string);

        return (
          <Input
            key={field.key}
            label={field.label}
            {...register(field.key)}
            value={clientFieldValue}
            disabled={!!selectedClient}
            placeholder={field.placeholder}
          />
        );
      }

      case 'services':
        // This would typically show a multi-select of services
        return (
          <div key={field.key} className="space-y-2">
            <label className="text-sm font-medium">{field.label}</label>
            <Textarea
              {...register(field.key)}
              placeholder="Enter services (one per line)"
              rows={3}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const [previewOpen, setPreviewOpen] = useState(false);

  // Watch all form values for preview
  const watchedValues = watch();

  const [templateContent, setTemplateContent] = React.useState<string>('');

  // Load template content from database or file
  React.useEffect(() => {
    const loadTemplate = async () => {
      // Prefer content from database
      if (template.content) {
        setTemplateContent(template.content);
        return;
      }

      // If no content in database, try loading from templatePath
          if (template.templatePath) {
            try {
              // First try to get it from static imports (works in builds)
              let content = getTemplateContent(template.templatePath);

              // If not found in static imports, try async loading
              if (!content) {
                content = await loadTemplateContent(template.templatePath);
              }

              if (content) {
                setTemplateContent(content);
              } else {
                logError('documents', new Error('Template content not found'), {
                  templatePath: template.templatePath,
                  templateId: template.id,
                });
                setTemplateContent(template.description || '<p>Template content not available.</p>');
              }
            } catch (error) {
              logError('documents', error, {
                source: 'DocumentGenerator.loadTemplate',
                templatePath: template.templatePath,
                templateId: template.id,
              });
              setTemplateContent(template.description || '<p>Template content not available.</p>');
            }
          } else {
        // No content or path - use description as fallback
        setTemplateContent(template.description || '<p>Document content will be generated from template.</p>');
      }
    };

    loadTemplate();
  }, [template]);

  // Process template content with current form values
  const processedPreview = useMemo(() => {
    if (!templateContent) return '<p>Loading template...</p>';

    const { title, clientId, useOrgLogo: useOrg, useClientLogo: useClient, ...dynamicFields } = watchedValues;

    const selectedClient = clientId ? clients.find((c) => c.id === clientId) : undefined;
    const clientAccount = findClientAccountForProject(selectedClient, organizations);
    const contactEmail = resolveProjectContactEmail(selectedClient, clientAccount);
    const orgLogoUrl = organization?.branding?.logoUrl || '';
    const clientLogoUrl = selectedClient?.logoUrl || '';

    const logoFields: Record<string, any> = {
      org_logo_url: useOrg && orgLogoUrl ? orgLogoUrl : '',
      client_logo_url: useClient && clientLogoUrl ? clientLogoUrl : '',
      show_org_logo: !!(useOrg && orgLogoUrl),
      show_client_logo: !!(useClient && clientLogoUrl),
    };

    const allDynamicFields: Record<string, any> = {
      ...(dynamicFields || {}),
      ...logoFields,
      org_name: organization?.name || '',
      org_email: organization?.email || '',
      client_name: selectedClient?.name || '',
      client_email: contactEmail,
    };

    return processTemplate(
      templateContent,
      allDynamicFields,
      title,
      selectedClient?.name,
      contactEmail
    );
  }, [templateContent, watchedValues, clients, organizations, organization]);

  const onSubmit = (data: any) => {
    const { title, clientId, useOrgLogo: useOrg, useClientLogo: useClient, ...dynamicFields } = data;
    if (!clientId) {
      alert('Please select a project.');
      return;
    }
    if (!title || title.trim() === '') {
      alert('Please enter a document title.');
      return;
    }

    let logoSource: DocumentLogoSource = 'none';
    if (useOrg && useClient) {
      logoSource = 'both';
    } else if (useOrg) {
      logoSource = 'organization';
    } else if (useClient) {
      logoSource = 'client';
    }

    onGenerate({ title: title.trim(), clientId, dynamicFields, logoSource });
  };

  return (
    <>
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Document Title"
        {...register('title', { required: 'Document title is required' })}
        error={errors.title?.message as string | undefined}
        placeholder="e.g., Service Agreement - Acme Corp"
      />

      <div className="space-y-2">
        <label className="text-sm font-medium">
          Project <span className="text-error">*</span>
        </label>
        {clientsLoading ? (
          <p className="text-sm text-gray-500">Loading projects...</p>
        ) : clients.length === 0 ? (
          <p className="text-sm text-gray-500">No projects available. Please create a project first.</p>
        ) : (
          <Select
            value={watch('clientId') || ''}
            onValueChange={(value) => setValue('clientId', value, { shouldValidate: true })}
            required
          >
            <SelectTrigger className={errors.clientId ? 'border-error' : ''}>
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {errors.clientId && (
          <p className="text-sm text-error">{errors.clientId.message as string}</p>
        )}
      </div>

      <div className="border-t pt-4 space-y-3">
        <h3 className="font-semibold">Logo Options</h3>
        <p className="text-sm text-muted-foreground">
          Choose which logo to display in the upper left corner of the document.
        </p>
        <div className="flex flex-col gap-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              {...register('useOrgLogo')}
              checked={!!useOrgLogo}
              onCheckedChange={(checked) => setValue('useOrgLogo', checked === true, { shouldValidate: false })}
              disabled={!organization?.branding?.logoUrl}
            />
            <span className="text-sm">
              Use client logo
              {!organization?.branding?.logoUrl && (
                <span className="ml-2 text-xs text-muted-foreground">
                  (no logo uploaded yet)
                </span>
              )}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              {...register('useClientLogo')}
              checked={!!useClientLogo}
              onCheckedChange={(checked) => setValue('useClientLogo', checked === true, { shouldValidate: false })}
              disabled={!selectedClientId}
            />
            <span className="text-sm">
              Use project logo
              {!selectedClientId && (
                <span className="ml-2 text-xs text-muted-foreground">
                  (select a project to see logo)
                </span>
              )}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            You can select both options to show both logos, or deselect both to show no logo.
          </p>
        </div>
      </div>

      <div className="border-t pt-4 space-y-4">
        <h3 className="font-semibold">Template Fields</h3>
        {template.dynamicFields.map((field) => (
          <div key={field.key}>
            {renderField(field)}
            {field.helpText && (
              <p className="text-xs text-gray-500 mt-1">{String(field.helpText)}</p>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting || externalIsSubmitting}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setPreviewOpen(true)}
          disabled={isSubmitting || externalIsSubmitting}
        >
          <Eye className="mr-2 h-4 w-4" />
          Preview
        </Button>
        <Button type="submit" loading={isSubmitting || externalIsSubmitting} disabled={isSubmitting || externalIsSubmitting || clients.length === 0}>
          Generate Document
        </Button>
      </div>
    </form>

    {/* Preview Dialog */}
    <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Document Preview</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            <p>This is a preview of how your document will look with the current field values.</p>
            {watch('title') && (
              <p className="mt-2 font-medium">Title: {watch('title')}</p>
            )}
          </div>

          <iframe
            srcDoc={processedPreview}
            className="w-full rounded-lg border bg-white"
            style={{ height: '600px' }}
            title="Document Preview"
            sandbox="allow-same-origin"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setPreviewOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>
  );
};
